<template>
  <div>
    <slot name="loading" :is-loading="isLoading">
      <OrderViewSkeleton v-if="isLoading" />
    </slot>

    <slot name="error" :error="error">
      <div
        v-if="error"
        class="min-h-screen bg-gray-50 flex items-center justify-center px-4"
      >
        <div class="text-center">
          <h3 class="text-red-600 text-2xl mb-4">{{ error }}</h3>
        </div>
      </div>
    </slot>

    <div v-if="!isLoading && !error" class="min-h-screen bg-gray-50">
      <LocationClosedBanner />
      <Navbar
        :cart-count="cartCount"
        @cart-click="onCartClick"
        :selected-location="selectedLocation"
        :is-cart-pinned="isSidebarVisible"
        :has-cart-updates="hasCartUpdates"
        :store-name="store.name"
        :store-logo="store.logo"
        :stores-count="store.storesCount"
        :show-location-selector="showLocationSelector"
        :table-name="tableName"
        :order-number="orderNumber"
        :top-offset="closedBannerOffset"
      />

      <div class="flex items-start justify-between">
        <div
          :class="[
            'w-full min-w-0 flex flex-col transition-all duration-300',
            isSidebarVisible ? 'lg:w-3/4' : 'lg:w-full',
            useTableCart ? '' : 'gap-8'
          ]"
        >
          <div :class="isSearching ? 'hidden md:block' : ''">
            <Hero
              :store="heroStore"
              :is-store-closed="isStoreClosed"
              :store-status="storeStatus"
            />
          </div>

          <div
            class="sticky z-30 bg-white shadow-sm"
            :style="{ top: stickyHeaderTop }"
          >
            <OrderOptionsBar
              :show-order-type-options="showOrderTypeOptions"
              :order-number="useTableCart ? null : orderNumber"
              :table-name="useTableCart ? null : tableName"
            />
            <div class="lg:hidden">
              <CategoryNav :is-vertical="false" />
            </div>
          </div>

          <main class="container mx-auto px-4 pt-8 -mt-10">
            <div class="flex items-start gap-8">
              <div
                class="hidden lg:block w-64 shrink-0 sticky overflow-y-auto custom-scrollbar"
                :style="verticalNavStyle"
              >
                <CategoryNav :is-vertical="true" />
              </div>

              <div class="flex-1 min-w-0 pb-20">
                <!-- Hide deals on mobile while searching -->
                <div :class="isSearching ? 'hidden md:block' : ''">
                  <slot name="before-menu" />
                </div>

                <template v-if="filteredMenu && filteredMenu.length > 0">
                  <MenuSection
                    v-for="category in filteredMenu"
                    :key="category.id"
                    :category="category"
                    :is-buy-now-enabled="isBuyNowEnabled"
                    :list-mode="isSearching"
                    @item-click="onItemClick"
                    @buy-now-click="onBuyNowClick"
                  />
                </template>
                <NoSearchResults v-else />

                <slot name="after-menu" />
              </div>
            </div>
          </main>

          <footer
            class="mt-auto py-10 border-t border-gray-200 text-gray-500 text-base bg-white"
          >
            <div class="container mx-auto px-4 flex flex-col gap-4">
              <div
                class="flex flex-col sm:flex-row items-center sm:items-center justify-between gap-4"
              >
                <p class="m-0">© {{ new Date().getFullYear() }} {{ store.name }}. All Rights Reserved.</p>
                <a
                  href="https://biyopos.com/?utm_source=customer-website&utm_medium=powered-by"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="inline-flex items-center justify-center gap-2 px-4 py-2 border border-gray-200 rounded-full no-underline text-gray-500 hover:text-gray-700 hover:border-gray-300 transition-colors"
                >
                  <img
                    src="@/assets/images/biyo_logo.png"
                    alt="Biyo POS"
                    class="h-6 w-auto"
                  />
                  <span class="text-sm">Powered by Biyo POS</span>
                </a>
              </div>
              <span class="text-xs text-gray-400 text-center">v{{ appVersion }}</span>
            </div>
          </footer>
        </div>

        <div
          v-if="isSidebarVisible"
          class="hidden lg:block h-screen sticky top-0 border-l border-gray-200 bg-white fadeIn-0-3s"
          style="width: 25%; z-index: 20"
        >
          <TableCartSidebar
            v-if="useTableCart"
            @update-quantity="onUpdateQuantity"
            @remove-item="onRemoveItem"
            @edit-item="onEditItem"
            @checkout="onCheckout"
            @view-check="onViewCheck"
            @unpin="onUnpin"
            :items="cartItems"
            :is-pinned="isCartPinned"
            :is-checkout-active="false"
            :is-store-closed="isStoreClosed"
          />
          <CartSidebar
            v-else
            :items="cartItems"
            :on-update-quantity="onUpdateQuantity"
            :on-remove-item="onRemoveItem"
            :on-edit-item="onEditItem"
            :on-checkout="onCheckout"
            :order-config="orderConfig"
            :location="selectedLocation"
            :is-pinned="isCartPinned"
            :on-unpin="onUnpin"
            :is-checkout-active="false"
            :is-store-closed="isStoreClosed"
          />
        </div>
      </div>

      <TableCartDrawer
        v-if="useTableCart"
        :is-open="isCartOpen"
        @close="onCartClose"
        @update-quantity="onUpdateQuantity"
        @remove-item="onRemoveItem"
        @edit-item="onEditItem"
        @checkout="onCheckout"
        @view-check="onViewCheck"
        @pin="onPin"
        :items="cartItems"
        :is-store-closed="isStoreClosed"
      />
      <CartDrawer
        v-else
        :is-open="isCartOpen"
        :on-close="onCartClose"
        :items="cartItems"
        :on-update-quantity="onUpdateQuantity"
        :on-remove-item="onRemoveItem"
        :on-edit-item="onEditItem"
        :on-checkout="onCheckout"
        :order-config="orderConfig"
        :location="selectedLocation"
        :on-pin="onPin"
        :is-store-closed="isStoreClosed"
      />

      <ItemModal
        :item="selectedItem"
        :on-close="onItemModalClose"
        :on-add-to-cart="onAddToCart"
        :editing-cart-id="editingCartId"
        :on-update-cart-item="onUpdateCartItem"
      />

      <LoactionIsClosedModal />

      <slot name="additional-content" />
    </div>
  </div>
</template>

<script>
import Navbar from "@/components/Navbar.vue";
import Hero from "@/components/Hero.vue";
import CategoryNav from "@/components/CategoryNav.vue";
import MenuSection from "@/components/MenuSection.vue";
import OrderOptionsBar from "@/components/OrderOptionsBar.vue";
import CartDrawer from "@/components/Cart/CartDrawer.vue";
import CartSidebar from "@/components/Cart/CartSidebar.vue";
import TableCartDrawer from "@/components/Cart/TableCartDrawer.vue";
import TableCartSidebar from "@/components/Cart/TableCartSidebar.vue";
import ItemModal from "@/components/ItemModal/ItemModal.vue";
import LoactionIsClosedModal from "@/components/Location/LoactionIsClosedModal.vue";
import LocationClosedBanner from "@/components/Location/LocationClosedBanner.vue";
import NoSearchResults from "@/components/NoSearchResults.vue";
import OrderViewSkeleton from "@/components/Skeletons/OrderViewSkeleton.vue";
import { useSelectedStore } from "@/composables/useSelectedStore";

export default {
  name: "OrderLayout",
  components: {
    Navbar,
    Hero,
    CategoryNav,
    MenuSection,
    OrderOptionsBar,
    CartDrawer,
    CartSidebar,
    TableCartDrawer,
    TableCartSidebar,
    ItemModal,
    LoactionIsClosedModal,
    LocationClosedBanner,
    NoSearchResults,
    OrderViewSkeleton
  },
  props: {
    isLoading: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: null
    },
    store: {
      type: Object,
      required: true
    },
    heroStore: {
      type: Object,
      required: true
    },
    selectedLocation: {
      type: Object,
      default: null
    },
    cartCount: {
      type: Number,
      default: 0
    },
    cartItems: {
      type: Array,
      default: () => []
    },
    isSidebarVisible: {
      type: Boolean,
      default: false
    },
    isCartOpen: {
      type: Boolean,
      default: false
    },
    isCartPinned: {
      type: Boolean,
      default: false
    },
    hasCartUpdates: {
      type: Boolean,
      default: false
    },
    filteredMenu: {
      type: Array,
      default: () => []
    },
    selectedItem: {
      type: Object,
      default: null
    },
    orderConfig: {
      type: Object,
      default: () => ({})
    },
    isStoreClosed: {
      type: Boolean,
      default: false
    },
    // Use table cart components
    useTableCart: {
      type: Boolean,
      default: false
    },
    showOrderTypeOptions: {
      type: Boolean,
      default: true
    },
    orderNumber: {
      type: [String, Number],
      default: null
    },
    tableName: {
      type: String,
      default: null
    },
    onViewCheck: {
      type: Function,
      default: null
    },
    onCartClick: {
      type: Function,
      required: true
    },
    onCartClose: {
      type: Function,
      required: true
    },
    onUpdateQuantity: {
      type: Function,
      required: true
    },
    onRemoveItem: {
      type: Function,
      required: true
    },
    onCheckout: {
      type: Function,
      required: true
    },
    onPin: {
      type: Function,
      required: true
    },
    onUnpin: {
      type: Function,
      required: true
    },
    onItemClick: {
      type: Function,
      required: true
    },
    onBuyNowClick: {
      type: Function,
      default: null
    },
    onItemModalClose: {
      type: Function,
      required: true
    },
    onAddToCart: {
      type: Function,
      required: true
    },
    editingCartId: {
      type: String,
      default: null
    },
    onEditItem: {
      type: Function,
      default: null
    },
    onUpdateCartItem: {
      type: Function,
      default: null
    },
    showLocationSelector: {
      type: Boolean,
      default: true
    },
    isBuyNowEnabled: {
      type: Boolean,
      default: false
    }
  },
  created() {
    this._selectedStore = useSelectedStore(this.$store);
  },
  computed: {
    appVersion() {
      return "2026.08.26.01";
    },
    isSearching() {
      const query = this.$store.getters.searchQuery || "";
      return query.trim().length > 0;
    },
    storeStatus() {
      if (this._selectedStore) {
        return this._selectedStore.getStoreStatus();
      }
      return "open";
    },
    shouldShowClosedBanner() {
      return (
        this.storeStatus === "closed" || this.storeStatus === "schedule_only"
      );
    },
    closedBannerOffset() {
      return this.shouldShowClosedBanner ? "39px" : "0px";
    },
    stickyHeaderTop() {
      return this.shouldShowClosedBanner ? "119px" : "80px";
    },
    verticalNavTop() {
      return this.shouldShowClosedBanner ? "179px" : "140px";
    },
    verticalNavStyle() {
      return {
        top: this.verticalNavTop,
        maxHeight: `calc(100vh - ${this.verticalNavTop})`
      };
    }
  }
};
</script>

<style>
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.fadeIn-0-3s {
  animation: fadeIn 0.3s ease-out;
}
</style>
